/**
 * Document Upload & Compliance Types
 * TASK-361: Document Management & Compliance System
 *
 * Types for secure upload links, quarantine documents, and compliance tracking.
 */

// ============================================================================
// Enums & Union Types
// ============================================================================

export type UploadLinkStatus = 'active' | 'expired' | 'completed' | 'revoked';

export type QuarantineStatus =
  | 'pending'
  | 'scanning'
  | 'clean'
  | 'infected'
  | 'accepted'
  | 'rejected';

export type DocumentComplianceStatus =
  | 'valid'
  | 'expiring_soon'
  | 'expired'
  | 'no_expiry';

export type DocType =
  | 'kyc'
  | 'portfolio'
  | 'risk-profile'
  | 'aml'
  | 'compliance'
  | 'contracts'
  | 'general';

// ============================================================================
// Secure Upload Link Types
// ============================================================================

export interface SecureUploadLink {
  id: string;
  account_id: string;
  client_id: string;
  client_name?: string;
  client_email?: string;
  secure_token?: string;
  portal_url?: string;
  document_types: DocType[];
  message_to_client?: string;
  max_uploads: number;
  uploads_used: number;
  status: UploadLinkStatus;
  expires_at: string;
  created_by: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  revoked_by?: string;
  revoked_at?: string;
}

export interface CreateSecureUploadLinkRequest {
  client_id: string;
  document_types?: DocType[];
  message_to_client?: string;
  max_uploads?: number;
  expires_in_days?: number;
  send_email?: boolean;
}

export interface SecureUploadLinkCreatedResponse {
  id: string;
  portal_url: string;
  secure_token: string;
  expires_at: string;
  email_sent: boolean;
  message: string;
}

export interface SecureUploadLinkListResponse {
  total: number;
  links: SecureUploadLink[];
}

export interface UploadLinkStats {
  total_links: number;
  active_links: number;
  expired_links: number;
  completed_links: number;
  revoked_links: number;
  total_uploads_received: number;
}

// ============================================================================
// Quarantine Document Types
// ============================================================================

export interface QuarantineDocument {
  id: string;
  upload_link_id: string;
  account_id: string;
  client_id: string;
  client_name?: string;
  filename: string;
  original_filename: string;
  file_size: number;
  file_size_formatted?: string;
  mime_type: string;
  doc_type: DocType;
  storage_key: string;
  quarantine_status: QuarantineStatus;
  scan_result?: Record<string, unknown>;
  reviewed_by?: string;
  reviewed_by_name?: string;
  reviewed_at?: string;
  rejection_reason?: string;
  final_document_id?: string;
  uploaded_at: string;
  uploader_ip?: string;
}

export interface QuarantineListResponse {
  total: number;
  pending_count: number;
  documents: QuarantineDocument[];
}

export interface AcceptDocumentRequest {
  doc_type?: DocType;
  expires_at?: string;
  description?: string;
}

export interface AcceptDocumentResponse {
  success: boolean;
  quarantine_id: string;
  document_id: string;
  message: string;
  // TASK-817: accepting starts a read of the document that outlives this response.
  // 'processing' means it is under way; 'unknown' means nothing was started.
  extraction_status?: 'processing' | 'unknown';
}

export interface RejectDocumentRequest {
  reason: string;
  allow_reupload?: boolean;
}

export interface RejectDocumentResponse {
  success: boolean;
  quarantine_id: string;
  reupload_allowed: boolean;
  message: string;
}

// ============================================================================
// Compliance Dashboard Types
// ============================================================================

export interface ComplianceSummary {
  total_documents: number;
  up_to_date: number;
  expiring_soon: number;
  expired: number;
  no_expiry: number;
  compliance_rate: number;
}

export interface ExpiringDocument {
  id: string;
  client_id: string;
  client_name?: string;
  doc_type: DocType;
  filename: string;
  expires_at: string;
  days_until_expiry: number;
  compliance_status: DocumentComplianceStatus;
  uploaded_at?: string;
}

export interface ExpiringDocumentsListResponse {
  total: number;
  days_ahead: number;
  documents: ExpiringDocument[];
}

export interface ComplianceByCategory {
  category: DocType;
  total: number;
  up_to_date: number;
  expiring_soon: number;
  expired: number;
  compliance_rate: number;
}

// ============================================================================
// Portal Types (for public upload portal)
// ============================================================================

export interface PortalInfo {
  link_id: string;
  client_email_masked: string;
  company_name: string;
  document_types: DocType[];
  message_to_client?: string;
  max_uploads: number;
  uploads_remaining: number;
  expires_at: string;
  is_expired: boolean;
  is_completed: boolean;
}

export interface VerifyOTPResponse {
  success: boolean;
  session_token?: string;
  session_expires_at?: string;
  uploads_remaining: number;
  message: string;
}

export interface UploadResponse {
  success: boolean;
  quarantine_id: string;
  filename: string;
  file_size: number;
  uploads_remaining: number;
  message: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

export function getDocTypeLabel(docType: DocType): string {
  const labels: Record<DocType, string> = {
    kyc: 'KYC Documents',
    portfolio: 'Portfolio Statements',
    'risk-profile': 'Risk Profile',
    aml: 'AML Documentation',
    compliance: 'Compliance Documents',
    contracts: 'Contracts',
    general: 'General Documents',
  };
  return labels[docType] || docType;
}

export function getUploadLinkStatusColor(status: UploadLinkStatus): string {
  const colors: Record<UploadLinkStatus, string> = {
    active: 'bg-green-100 text-green-800',
    expired: 'bg-gray-100 text-gray-800',
    completed: 'bg-blue-100 text-blue-800',
    revoked: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getQuarantineStatusColor(status: QuarantineStatus): string {
  const colors: Record<QuarantineStatus, string> = {
    pending: 'bg-amber-100 text-amber-800',
    scanning: 'bg-blue-100 text-blue-800',
    clean: 'bg-green-100 text-green-800',
    infected: 'bg-red-100 text-red-800',
    accepted: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getComplianceStatusColor(status: DocumentComplianceStatus): string {
  const colors: Record<DocumentComplianceStatus, string> = {
    valid: 'bg-green-100 text-green-800',
    expiring_soon: 'bg-amber-100 text-amber-800',
    expired: 'bg-red-100 text-red-800',
    no_expiry: 'bg-gray-100 text-gray-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function formatDaysUntilExpiry(days: number): string {
  if (days < 0) {
    return `Expired ${Math.abs(days)} days ago`;
  } else if (days === 0) {
    return 'Expires today';
  } else if (days === 1) {
    return 'Expires tomorrow';
  } else {
    return `Expires in ${days} days`;
  }
}

export function getQuarantineStatusLabel(status: QuarantineStatus): string {
  const labels: Record<QuarantineStatus, string> = {
    pending: 'Pending Review',
    scanning: 'Scanning...',
    clean: 'Ready for Review',
    infected: 'Security Issue',
    accepted: 'Accepted',
    rejected: 'Rejected',
  };
  return labels[status] || status;
}

export function getComplianceRateTextColor(rate: number): string {
  if (rate >= 80) return 'text-green-600';
  if (rate >= 60) return 'text-amber-600';
  return 'text-red-600';
}

export function getComplianceRateBarColor(rate: number): string {
  if (rate >= 80) return 'bg-green-500';
  if (rate >= 60) return 'bg-amber-500';
  return 'bg-red-500';
}

export function getUploadLinkStatusLabel(status: UploadLinkStatus): string {
  const labels: Record<UploadLinkStatus, string> = {
    active: 'Active',
    expired: 'Expired',
    completed: 'Completed',
    revoked: 'Revoked',
  };
  return labels[status] || status;
}
